// BM-H5047 (Vanessa's spec, 4 Nov 2026): "Submit for review" on the matter
// page and the supervision board. Opens a card in Ready for review (or
// Drafting) with a link to the live file in SharePoint. The board holds
// links, never files, so there is deliberately no upload control here.
import { useEffect, useState } from "react";
import axios from "axios";
import { Send, Loader2, FolderOpen, Folder, FileText, ChevronLeft } from "lucide-react";
import { API } from "../config/api";
import { FormattedTextarea } from "./supervision/RichText";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";

export const ITEM_TYPES = [
  { key: "letter", label: "Letter" },
  { key: "email", label: "Email" },
  { key: "advice", label: "Advice" },
  { key: "memo", label: "Memo" },
  { key: "court", label: "Court document" },
  { key: "other", label: "Other" },
];

function newRequestId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function looksLikeSharePoint(url) {
  try {
    const u = new URL((url || "").trim());
    return u.protocol === "https:" && /(^|\.)sharepoint\.com$/i.test(u.hostname);
  } catch {
    return false;
  }
}

// Vanessa, 4 Nov: an email can go on a card by its Outlook link.
const OUTLOOK_HOSTS = ["outlook.office.com", "outlook.office365.com", "outlook.cloud.microsoft"];

export function isOutlookLink(url) {
  try {
    const u = new URL((url || "").trim());
    return u.protocol === "https:" && OUTLOOK_HOSTS.includes(u.hostname.toLowerCase());
  } catch {
    return false;
  }
}

export function linkAccepted(url) {
  return looksLikeSharePoint(url) || isOutlookLink(url);
}

// Vanessa, 4 Nov: pick the document from the matter's SharePoint folder
// instead of copying its link. Lists the folder; never copies a file.
function FolderPicker({ matterRef, onPick }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async (folderId = "") => {
    setBusy(true); setError("");
    try {
      const { data: d } = await axios.get(`${API}/supervision/matter-files`,
        { params: { matter_reference: matterRef, folder_id: folderId } });
      setData(d);
    } catch (err) {
      setError(errorMessage(err, "The matter folder could not be read. Paste the link instead."));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(""); }, [matterRef]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="rounded-md border border-white/10 bg-slate-950/60 max-h-64 overflow-y-auto text-xs"
      data-testid="sfr-picker">
      <div className="flex items-center gap-2 px-2 py-1.5 border-b border-white/10 text-slate-400 sticky top-0 bg-slate-950">
        {data && data.trail.length > 0 && (
          <button type="button" className="hover:text-white" onClick={() => load(data.parent_id)}
            data-testid="sfr-picker-up"><ChevronLeft className="w-4 h-4" /></button>
        )}
        <span className="truncate">{data ? [data.folder_name, ...data.trail].join(" / ") : "Matter folder"}</span>
        {busy && <Loader2 className="w-3 h-3 animate-spin ml-auto" />}
      </div>
      {error && <p className="p-2 text-status-red" data-testid="sfr-picker-error">{error}</p>}
      {data && data.items.length === 0 && <p className="p-2 text-slate-500">This folder is empty.</p>}
      {data && data.items.map(it => (
        <button type="button" key={it.id} data-testid={`sfr-pick-${it.name}`}
          className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-white/5"
          onClick={() => (it.is_folder ? load(it.id) : onPick(it))}>
          {it.is_folder ? <Folder className="w-4 h-4 text-indigo-300 shrink-0" />
            : <FileText className="w-4 h-4 text-slate-400 shrink-0" />}
          <span className="truncate text-slate-200">{it.name}</span>
          {!it.is_folder && it.modified_at && (
            <span className="ml-auto shrink-0 text-slate-500">
              {new Date(it.modified_at).toLocaleDateString("en-GB")}{it.modified_by ? ` · ${it.modified_by}` : ""}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

const fieldCls = "w-full rounded-md border border-white/10 bg-bonsai-bg px-3 py-2 text-sm text-white";

export default function SubmitForReviewDialog({ matterRef = "", onSubmitted, trigger }) {
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState(matterRef);
  const [title, setTitle] = useState("");
  const [itemType, setItemType] = useState("letter");
  const [link, setLink] = useState("");
  const [note, setNote] = useState("");
  const [clientFacing, setClientFacing] = useState(true);
  const [dueDate, setDueDate] = useState("");
  const [startIn, setStartIn] = useState("ready_for_review");
  const [picking, setPicking] = useState(false);
  const [requestId, setRequestId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const start = () => {
    setRef(matterRef); setTitle(""); setItemType("letter"); setLink(""); setNote("");
    setClientFacing(true); setDueDate(""); setStartIn("ready_for_review");
    setRequestId(newRequestId()); setError(""); setPicking(false); setOpen(true);
  };

  const linkOk = linkAccepted(link);
  const ready = ref.trim() && title.trim() && linkOk && !busy;

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/supervision/cards`, {
        matter_reference: ref.trim(), title: title.trim(), item_type: itemType,
        sharepoint_url: link.trim(), note, client_facing: clientFacing || itemType === "court",
        court: itemType === "court", due_date: dueDate || null, start_in: startIn,
        request_id: requestId,
      });
      setOpen(false);
      onSubmitted && onSubmitted(data.card);
    } catch (err) {
      setError(errorMessage(err, "The card could not be created."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {trigger ? trigger(start) : (
        <Button variant="outline" size="sm" onClick={start} data-testid="submit-for-review-open">
          <Send className="w-4 h-4 mr-1" /> Submit for review
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="submit-for-review-dialog">
          <DialogHeader>
            <DialogTitle>Submit for review</DialogTitle>
            <DialogDescription>
              Paste the link to the document in the matter's SharePoint folder, or to the email in Outlook. The board keeps the link, never a copy.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {!matterRef && (
              <label className="block">
                <span className="text-xs text-slate-400">Matter reference</span>
                <Input value={ref} onChange={e => setRef(e.target.value.toUpperCase())}
                  placeholder="BL.VEC.002.1234" data-testid="sfr-matter" />
              </label>
            )}
            <label className="block">
              <span className="text-xs text-slate-400">What is it?</span>
              <Input value={title} onChange={e => setTitle(e.target.value)} maxLength={200}
                placeholder="Letter to Slater re WP counter" data-testid="sfr-title" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-slate-400">Type</span>
                <select className={fieldCls} value={itemType} onChange={e => setItemType(e.target.value)}
                  data-testid="sfr-type">
                  {ITEM_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-slate-400">Due (optional)</span>
                <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="sfr-due" />
              </label>
            </div>
            <label className="block">
              <span className="text-xs text-slate-400 flex items-center">
                Link (SharePoint document or Outlook email)
                {ref.trim() && (
                  <button type="button" className="ml-auto text-indigo-300 hover:text-white flex items-center gap-1"
                    onClick={e => { e.preventDefault(); setPicking(!picking); }} data-testid="sfr-pick-open">
                    <FolderOpen className="w-3.5 h-3.5" />{picking ? "Close folder" : "Pick from matter folder"}
                  </button>
                )}
              </span>
              {picking && (
                <div className="mb-2">
                  <FolderPicker matterRef={ref.trim()} onPick={it => {
                    setLink(it.web_url); setPicking(false);
                    if (!title.trim()) setTitle(it.name.replace(/\.[^.]+$/, ""));
                    if (/\.(msg|eml)$/i.test(it.name) && itemType === "letter") setItemType("email");
                  }} />
                </div>
              )}
              <Input value={link} onChange={e => {
                  setLink(e.target.value);
                  if (isOutlookLink(e.target.value) && itemType === "letter") setItemType("email");
                }}
                placeholder="https://...sharepoint.com/... or the email's Outlook link" data-testid="sfr-link" />
              {link && !linkOk && (
                <span className="text-xs text-status-red" data-testid="sfr-link-error">
                  This must be the document's SharePoint link, or the email's Outlook link (open it in Outlook on the web and copy the address).
                </span>
              )}
            </label>
            <div className="block">
              <span className="text-xs text-slate-400">Covering note (optional)</span>
              <FormattedTextarea className={fieldCls} rows={4} value={note} onChange={setNote}
                maxLength={4000} testId="sfr-note" />
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-slate-300">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={clientFacing || itemType === "court"} disabled={itemType === "court"}
                  onChange={e => setClientFacing(e.target.checked)} data-testid="sfr-client-facing" />
                Goes to the client or the other side
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={startIn === "drafting"}
                  onChange={e => setStartIn(e.target.checked ? "drafting" : "ready_for_review")}
                  data-testid="sfr-drafting" />
                Still drafting (show it as coming)
              </label>
            </div>
            <p className="text-[11px] text-slate-500">Write it as if the client will read it.</p>
            {error && <p className="text-status-red text-xs" data-testid="sfr-error">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={!ready} data-testid="sfr-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {startIn === "drafting" ? "Add to Drafting" : "Submit for review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
